/**
 * Report filtering utilities
 *
 * Extracted from useAdminReports.js for testability.
 * Contains pure functions for filtering report arrays.
 */

/**
 * Filter reports by HRO (High Reliability Organization) values
 * @param {Array} reports - Array of report objects
 * @param {Array} hroValues - HRO values to filter by
 * @returns {Array} Filtered reports
 */
export function filterByHRO(reports, hroValues) {
  if (!hroValues?.length) return reports;

  return reports.filter((report) => {
    for (const selectedHRO of hroValues) {
      // Check explicit HROs
      if (report.HROs?.includes(selectedHRO)) return true;
      // Fall back to AI-detected HROs if no explicit ones
      if ((!report.HROs || !report.HROs.length) && report.HRO_AI?.includes(selectedHRO)) {
        return true;
      }
    }
    return false;
  });
}

/**
 * Filter reports by KPI and/or Location values
 * @param {Array} reports - Array of report objects
 * @param {Array} kpiValues - KPI/location values to filter by
 * @returns {Array} Filtered reports
 */
export function filterByKPI(reports, kpiValues) {
  if (!kpiValues?.length) return reports;

  let filteredReports = reports;
  const matchedKPIs = [];

  // First pass: filter by KPIs
  const kpiFiltered = reports.filter((report) => {
    for (const selectedKPI of kpiValues) {
      if (report.KPIs?.includes(selectedKPI)) {
        matchedKPIs.push(selectedKPI);
        return true;
      }
      if (report.KPI_AI === selectedKPI) {
        matchedKPIs.push(selectedKPI);
        return true;
      }
    }
    return false;
  });

  if (kpiFiltered.length) {
    filteredReports = kpiFiltered;
  }

  // Second pass: filter by locations (values not matched as KPIs)
  const locationValues = kpiValues.filter((value) => !matchedKPIs.includes(value));
  if (locationValues.length) {
    filteredReports = filteredReports.filter((report) => {
      for (const selectedLocation of locationValues) {
        if (report.locations?.includes(selectedLocation)) return true;
      }
      return false;
    });
  }

  return filteredReports;
}

/**
 * Filter to only include reports without responses/comments
 * @param {Array} reports - Array of report objects
 * @returns {Array} Reports without responses
 */
export function filterUnrespondedOnly(reports) {
  return reports.filter(
    (report) => report.comments.length === 0 || report.comments[0] === 'No comments yet'
  );
}

/**
 * Filter reports by organization
 * @param {Array} reports - Array of report objects
 * @param {Array} organizations - Organizations to filter by
 * @returns {Array} Filtered reports
 */
export function filterByOrganization(reports, organizations) {
  if (!organizations?.length) return reports;

  return reports.filter((report) => organizations.includes(report.organization));
}

/**
 * Apply multiple filters to reports
 * @param {Array} reports - Array of report objects
 * @param {Object} filters - Filter options
 * @param {Array} [filters.hroValues] - HRO values to filter by
 * @param {Array} [filters.kpiValues] - KPI values to filter by
 * @param {boolean} [filters.unrespondedOnly] - Only include unresponded reports
 * @param {Array} [filters.organizations] - Organizations to filter by
 * @returns {Array} Filtered reports
 */
export function applyFilters(reports, filters = {}) {
  let result = reports;

  if (filters.hroValues?.length) {
    result = filterByHRO(result, filters.hroValues);
  }

  if (filters.kpiValues?.length) {
    result = filterByKPI(result, filters.kpiValues);
  }

  if (filters.unrespondedOnly) {
    result = filterUnrespondedOnly(result);
  }

  if (filters.organizations?.length) {
    result = filterByOrganization(result, filters.organizations);
  }

  return result;
}
